/**
 * Flows hooks using React Query
 * Provides data fetching, caching, and mutations for flows
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  flowsService,
  type Flow,
  type CreateFlowRequest,
  type FlowParams,
  type FlowNodeParams,
  type FlowEdgeParams,
} from '@/services/flows';

// Query keys for cache management
export const flowsKeys = {
  all: ['flows'] as const,
  lists: () => [...flowsKeys.all, 'list'] as const,
  list: () => [...flowsKeys.lists()] as const,
  details: () => [...flowsKeys.all, 'detail'] as const,
  detail: (id: string) => [...flowsKeys.details(), id] as const,
  snapshots: () => [...flowsKeys.all, 'snapshot'] as const,
  snapshot: (id: string) => [...flowsKeys.snapshots(), id] as const,
  flowData: () => [...flowsKeys.all, 'flowData'] as const,
  flowDataByPeriod: (periodType: string, periodStart: string) =>
    [...flowsKeys.flowData(), periodType, periodStart] as const,
  nodeDetails: () => [...flowsKeys.all, 'nodeDetail'] as const,
  nodeDetail: (periodType: string, periodStart: string, nodeId: string) =>
    [...flowsKeys.nodeDetails(), periodType, periodStart, nodeId] as const,
  edgeDetails: () => [...flowsKeys.all, 'edgeDetail'] as const,
  edgeDetail: (periodType: string, periodStart: string, sourceId: string, targetId: string) =>
    [...flowsKeys.edgeDetails(), periodType, periodStart, sourceId, targetId] as const,
  summary: () => [...flowsKeys.all, 'summary'] as const,
};

/**
 * Hook to fetch all flows
 */
export function useFlows() {
  return useQuery({
    queryKey: flowsKeys.list(),
    queryFn: () => flowsService.getAll(),
  });
}

/**
 * Retry policy for the queries that read one flow (GET /flows/{id}).
 *
 * Never retries a 404 (deleted flow: prevents console spam after delete) or a
 * 429 (the household's flow calculation limit, WP-CORE-9: a retry cannot
 * succeed before the window ends, and the retries' backoff delayed the "try
 * again in N minutes" message). The API client throws ApiClientError with
 * `status`; the axios-style `response.status` the old check looked for is
 * still recognised. Otherwise up to 3 attempts.
 */
export function shouldRetryFlowRead(failureCount: number, error: unknown): boolean {
  if (error && typeof error === 'object') {
    const status =
      (error as { status?: number }).status ??
      (error as { response?: { status?: number } }).response?.status;
    if (status === 404 || status === 429) {
      return false;
    }
  }
  return failureCount < 3;
}

/**
 * Hook to fetch a specific flow by ID
 */
export function useFlow(flowId: string | undefined) {
  return useQuery({
    queryKey: flowsKeys.detail(flowId ?? ''),
    queryFn: () => flowsService.getById(flowId!),
    enabled: !!flowId,
    retry: shouldRetryFlowRead,
  });
}

/**
 * Hook to fetch flow snapshot by ID
 */
export function useFlowSnapshot(flowId: string | undefined) {
  return useQuery({
    queryKey: flowsKeys.snapshot(flowId ?? ''),
    queryFn: () => flowsService.getSnapshot(flowId!),
    enabled: !!flowId,
    // Flow data can be stale for 1 minute before refetching
    staleTime: 1000 * 60,
    retry: shouldRetryFlowRead,
  });
}

/**
 * Hook to fetch flow data by period parameters
 */
export function useFlowByPeriod(params: FlowParams | undefined) {
  return useQuery({
    queryKey: flowsKeys.flowDataByPeriod(
      params?.periodType ?? '',
      params?.periodStart ?? ''
    ),
    queryFn: () => flowsService.getFlow(params!),
    enabled: !!params?.periodType && !!params?.periodStart,
    staleTime: 1000 * 60,
  });
}

/**
 * Hook to fetch node detail for drill-down
 */
export function useFlowNodeDetail(params: FlowNodeParams | undefined) {
  return useQuery({
    queryKey: flowsKeys.nodeDetail(
      params?.periodType ?? '',
      params?.periodStart ?? '',
      params?.nodeId ?? ''
    ),
    queryFn: () => flowsService.getNodeDetail(params!),
    enabled: !!params?.periodType && !!params?.periodStart && !!params?.nodeId,
  });
}

/**
 * Hook to fetch edge detail for drill-down
 */
export function useFlowEdgeDetail(params: FlowEdgeParams | undefined) {
  return useQuery({
    queryKey: flowsKeys.edgeDetail(
      params?.periodType ?? '',
      params?.periodStart ?? '',
      params?.sourceId ?? '',
      params?.targetId ?? ''
    ),
    queryFn: () => flowsService.getEdgeDetail(params!),
    enabled:
      !!params?.periodType &&
      !!params?.periodStart &&
      !!params?.sourceId &&
      !!params?.targetId,
  });
}

/**
 * Hook to fetch edge transactions
 */
export function useFlowEdgeTransactions(params: FlowEdgeParams | undefined) {
  return useQuery({
    queryKey: [
      'flows',
      'edgeTransactions',
      params?.periodType,
      params?.periodStart,
      params?.sourceId,
      params?.targetId,
      params?.page,
    ],
    queryFn: () => flowsService.getEdgeTransactions(params!),
    enabled:
      !!params?.periodType &&
      !!params?.periodStart &&
      !!params?.sourceId &&
      !!params?.targetId,
  });
}

/**
 * Hook to fetch flow summary for multiple periods
 */
export function useFlowSummary(periodType: string | undefined, count?: number) {
  return useQuery({
    queryKey: [...flowsKeys.summary(), periodType, count],
    queryFn: () =>
      flowsService.getSummary({
        periodType: periodType as 'month' | 'quarter' | 'year' | 'custom',
        count,
      }),
    enabled: !!periodType,
  });
}

/**
 * Hook to create a new flow
 */
export function useCreateFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFlowRequest) => flowsService.create(data),
    onSuccess: (newFlow: Flow) => {
      // Invalidate flows list to refetch
      queryClient.invalidateQueries({ queryKey: flowsKeys.lists() });

      // Optionally set the new flow in cache
      queryClient.setQueryData(flowsKeys.detail(newFlow.id), newFlow);
    },
  });
}

/**
 * Hook to update a flow
 */
export function useUpdateFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string } }) =>
      flowsService.update(id, data),
    onSuccess: (updatedFlow: Flow) => {
      // Update the flow in cache
      queryClient.setQueryData(flowsKeys.detail(updatedFlow.id), updatedFlow);
      // Invalidate flows list
      queryClient.invalidateQueries({ queryKey: flowsKeys.lists() });
    },
  });
}

/**
 * Hook to delete a flow
 */
export function useDeleteFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (flowId: string) => flowsService.delete(flowId),
    onMutate: async (flowId) => {
      // Cancel any in-flight queries for this flow BEFORE deletion
      // This prevents 404 errors from queries that are mid-flight or about to retry
      await queryClient.cancelQueries({ queryKey: flowsKeys.detail(flowId) });
      await queryClient.cancelQueries({ queryKey: flowsKeys.snapshot(flowId) });

      // Remove from cache immediately (optimistic)
      queryClient.removeQueries({ queryKey: flowsKeys.detail(flowId) });
      queryClient.removeQueries({ queryKey: flowsKeys.snapshot(flowId) });
    },
    onSuccess: () => {
      // Invalidate flows list to refetch without the deleted flow
      queryClient.invalidateQueries({ queryKey: flowsKeys.lists() });
    },
  });
}

/**
 * Hook to refresh/recompute flow data
 * @deprecated Use useRegenerateFlow instead for atomic regeneration
 */
export function useRefreshFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: FlowParams) => flowsService.refresh(params),
    onSuccess: (_result, params) => {
      // Invalidate flow data for the period
      queryClient.invalidateQueries({
        queryKey: flowsKeys.flowDataByPeriod(params.periodType, params.periodStart),
      });
    },
  });
}

/**
 * Hook to force regeneration of a flow snapshot.
 *
 * This is an atomic operation that regenerates the flow with current
 * transaction data and returns fresh data in a single request.
 * Use this when the user explicitly clicks the refresh button.
 *
 * On success, the cache is updated with the fresh snapshot data.
 */
export function useRegenerateFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (flowId: string) => flowsService.regenerate(flowId),
    onSuccess: (freshSnapshot, flowId) => {
      // Update the snapshot cache with fresh data
      queryClient.setQueryData(flowsKeys.snapshot(flowId), freshSnapshot);

      // Also invalidate the flow detail in case status changed
      queryClient.invalidateQueries({ queryKey: flowsKeys.detail(flowId) });

      // Invalidate flow list to reflect any status changes
      queryClient.invalidateQueries({ queryKey: flowsKeys.lists() });
    },
  });
}

/**
 * Convenience hook for current month flow
 */
export function useCurrentMonthFlow() {
  return useQuery({
    queryKey: [...flowsKeys.flowData(), 'currentMonth'],
    queryFn: () => flowsService.getCurrentMonth(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Convenience hook for monthly summary
 */
export function useMonthlySummary(count: number = 6) {
  return useQuery({
    queryKey: [...flowsKeys.summary(), 'monthly', count],
    queryFn: () => flowsService.getMonthlySummary(count),
    staleTime: 1000 * 60 * 5,
  });
}

export default {
  useFlows,
  useFlow,
  useFlowSnapshot,
  useFlowByPeriod,
  useFlowNodeDetail,
  useFlowEdgeDetail,
  useFlowEdgeTransactions,
  useFlowSummary,
  useCreateFlow,
  useUpdateFlow,
  useDeleteFlow,
  useRefreshFlow,
  useRegenerateFlow,
  useCurrentMonthFlow,
  useMonthlySummary,
};
